import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  User,
  CreditCard,
  AppWindow,
  Key,
  X,
} from 'lucide-react'
import { useAuth } from '@usoftware/auth-react'
import { cn } from '@/lib/utils'
import { useOrg } from '@/contexts'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Button,
  Separator,
  Avatar,
} from '@/components/ui'

interface NavItem {
  icon: React.ElementType
  label: string
  href: string
}

const navItems: NavItem[] = [
  { icon: LayoutDashboard, label: 'Dashboard', href: '/' },
  { icon: User, label: 'Account', href: '/account' },
  { icon: CreditCard, label: 'Billing', href: '/billing' },
  { icon: AppWindow, label: 'Apps', href: '/apps' },
  { icon: Key, label: 'API Keys', href: '/api-keys' },
]

interface MobileNavProps {
  open: boolean
  onClose: () => void
}

export function MobileNav({ open, onClose }: MobileNavProps) {
  const location = useLocation()
  const { name: orgName, branding } = useOrg()
  const { user, logout } = useAuth()

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'User'
  const email = user?.email || ''
  const avatarUrl = user?.avatarUrl || null

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="p-4 border-b border-[var(--border-subtle)]">
          <SheetTitle className="flex items-center gap-2">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={orgName || 'Logo'}
                className="h-6 w-auto"
              />
            ) : (
              <span className="text-base font-semibold">
                {orgName || 'u.software'}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.href)

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  active
                    ? 'bg-[var(--bg-elevated-2)] text-[var(--text-primary)]'
                    : 'text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-primary)]'
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        <Separator />

        {/* User info */}
        <div className="p-4">
          <div className="flex items-center gap-3">
            <Avatar
              src={avatarUrl}
              fallback={displayName}
              size="md"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                {displayName}
              </p>
              <p className="text-xs text-[var(--text-secondary)] truncate">
                {email}
              </p>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" size="sm" className="flex-1" asChild>
              <Link to="/account" onClick={onClose}>
                Settings
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={async () => {
                onClose()
                await logout()
              }}
            >
              Log out
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
